import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import toast, { Toaster } from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { Pencil, Trash2, Plus, ChevronUp, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import {
  fetchLmsEmployees,
  updateLmsEmployee,
  deleteLmsEmployee,
  syncLmsEmployeesToHrms,
  setPageNumber,
} from "../../redux/erpSlice";
import AddEmployeeModal from "../../modals/Hrms/AddEmployeeModal";
import EmployeeDetailsCard from "../../modals/Hrms/EmployeeDetailsCard";

const EmployeeDetails = () => {
  const dispatch = useDispatch();
  const { lmsEmployees, loading, totalCount, pageNumber, pageSize } = useSelector((state) => state.lmsEmployees);

  const { register, handleSubmit, reset, setValue } = useForm();
  const [searchTerm, setSearchTerm] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const [expandedRowIndex, setExpandedRowIndex] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [currentEmployeeEmail, setCurrentEmployeeEmail] = useState(null);
  const [currentPicture, setCurrentPicture] = useState(null);

  useEffect(() => {
    dispatch(fetchLmsEmployees({ pageNumber, pageSize }))
      .then((result) => {
        if (result.meta.requestStatus === "fulfilled") {
          dispatch(syncLmsEmployeesToHrms(result.payload.employees));
        }
      });
  }, [dispatch, pageNumber, pageSize]);

  // Calculate dynamic card data based on actual employee data
  const getCardData = () => {
    const totalEmployees = totalCount || 0;
    const teachers = lmsEmployees.filter(emp => emp.userType === "Teacher").length;
    const management = lmsEmployees.filter(emp => emp.userType === "Management").length;
    const activeEmployees = lmsEmployees.filter(emp => emp.isActive === true).length;
    const inactiveEmployees = lmsEmployees.filter(emp => emp.isActive === false).length;
    const resignedEmployees = lmsEmployees.filter(emp => emp.userStatus === "Resigned").length;

    return [
      { title: "Total Employees", value: totalEmployees, icon: "👥", bgColor: "bg-blue-100" },
      { title: "Teachers", value: teachers, icon: "👨‍🏫", bgColor: "bg-green-100" },
      { title: "Management", value: management, icon: "👔", bgColor: "bg-purple-100" },
      { title: "Active", value: activeEmployees, icon: "✅", bgColor: "bg-emerald-100" },
      { title: "Inactive", value: inactiveEmployees, icon: "❌", bgColor: "bg-red-100" },
      { title: "Resigned", value: resignedEmployees, icon: "🚶‍♂️", bgColor: "bg-orange-100" },
    ];
  };

  const onSubmit = async (data) => {
    const updatedEmployee = {
      firstName: data.firstName,
      lastName: data.lastName,
      fatherName: data.fatherName,
      gender: data.gender,
      maritalStatus: data.maritalStatus,
      email: data.email,
      phone: data.phone,
      location: data.location,
      cnic: data.cnic,
      dob: data.dob,
      emergencyContact: data.emergencyContact,
      contactRelationShip: data.contactRelationShip,
      address: data.address,
      religion: data.religion,
      department: data.department,
      designation: data.designation,
      profilePicture: currentPicture || "https://via.placeholder.com/150",
      isActive: data.isActive,
      userRole: data.userRole,
      userType: data.userType,
      userStatus: data.userStatus,
    };

    if (isEdit) {
      dispatch(updateLmsEmployee({ email: currentEmployeeEmail, updatedData: updatedEmployee }));
      toast.success("Employee updated successfully");
    } else {
      toast.error("Adding new employees is not supported for LMS employees");
      return;
    }

    reset();
    setIsModalOpen(false);
    setIsEdit(false);
  };

  const handleEdit = (email) => {
    const employee = lmsEmployees.find((emp) => emp.email === email);
    if (!employee) {
      console.error("Employee not found for email:", email);
      return;
    }

    Object.keys(employee).forEach((key) => setValue(key, employee[key]));

    setCurrentEmployeeEmail(email);
    setCurrentPicture(employee.profilePicture);
    setIsEdit(true);
    setIsModalOpen(true);
  };

  const handleDelete = (email) => {
    dispatch(deleteLmsEmployee(email));
    toast.success("Employee deleted successfully");
  };

  const toggleView = (index) => {
    setExpandedRowIndex(expandedRowIndex === index ? null : index);
  };

  // Pagination handlers
  const handlePreviousPage = () => {
    if (pageNumber > 1) {
      dispatch(setPageNumber(pageNumber - 1));
    }
  };

  const handleNextPage = () => {
    const totalPages = Math.ceil(totalCount / pageSize);
    if (pageNumber < totalPages) {
      dispatch(setPageNumber(pageNumber + 1));
    }
  };

  const handlePageClick = (page) => {
    dispatch(setPageNumber(page));
  };

  // Calculate pagination info
  const totalPages = Math.ceil(totalCount / pageSize);
  const startItem = (pageNumber - 1) * pageSize + 1;
  const endItem = Math.min(pageNumber * pageSize, totalCount);

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const delta = 2; // Number of pages to show on each side of current page
    const range = [];
    const rangeWithDots = [];

    for (let i = Math.max(2, pageNumber - delta); i <= Math.min(totalPages - 1, pageNumber + delta); i++) {
      range.push(i);
    }

    if (pageNumber - delta > 2) {
      rangeWithDots.push(1, '...');
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (pageNumber + delta < totalPages - 1) {
      rangeWithDots.push('...', totalPages);
    } else {
      rangeWithDots.push(totalPages);
    }

    return rangeWithDots;
  };

  const filteredEmployees = lmsEmployees.filter((employee) => {
    const fullName = `${employee.firstName} ${employee.lastName}`.toLowerCase();
    return (
      (fullName.includes(searchTerm.toLowerCase()) ||
        employee.email.includes(searchTerm)) &&
      (locationFilter === "" || employee.location === locationFilter)
    );
  });

  const cardsData = getCardData();

  return (
    <div className="container mx-auto p-4 border border-[#241763]">
      {/* Dynamic Cards Section */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        {cardsData.map((card, index) => (
          <div
            key={index}
            className={`flex items-center justify-between p-4 rounded shadow border`}
          >
            <div className="flex flex-col space-y-2">
              <span className="text-2xl font-bold text-gray-800">{card.value}</span>
              <span className="text-sm text-gray-600 font-medium">{card.title}</span>
            </div>
            <div className={`text-xl p-2 rounded-full ${card.bgColor}`}>
              {card.icon}
            </div>
          </div>
        ))}
      </div>

      <div className="mb-4 flex flex-col md:flex-row gap-4 items-center w-full">
        <input
          type="text"
          className="p-2 border border-gray-300 rounded w-full md:w-3/6"
          placeholder="Search by name or email"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <select
          className="p-2 border border-gray-300 rounded w-full md:w-2/6"
          value={locationFilter}
          onChange={(e) => setLocationFilter(e.target.value)}
        >
          <option value="">All Locations</option>
          {[...new Set(lmsEmployees.map((e) => e.location))].filter(loc => loc).map((loc, i) => (
            <option key={i} value={loc}>
              {loc}
            </option>
          ))}
        </select>
      </div>

      <AddEmployeeModal
        isModalOpen={isModalOpen}
        isEdit={isEdit}
        onSubmit={onSubmit}
        handleSubmit={handleSubmit}
        setIsModalOpen={setIsModalOpen}
        register={register}
        setValue={setValue}
        reset={reset}
        currentPicture={currentPicture}
      />

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#241763]"></div>
          <span className="ml-2 text-[#241763]">Loading employees...</span>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="table-auto w-full mt-6">
          <thead className="py-4 border-b font-bold">
            <tr>
              <th>Profile</th>
              <th>Name</th>
              <th>Gender</th>
              <th>Email</th>
              <th>Type</th>
              <th>Phone</th>
              <th>DOB</th>
              <th>Location</th>
              <th>Active</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody className="text-sm">
            {!loading && filteredEmployees.map((employee, index) => (
              <React.Fragment key={employee.email}>
                <tr className="p-2 py-2 hover:bg-gray-200 space-y-2">
                  <td>
                    <img
                      src={
                        employee.profilePicture ||
                        "https://via.placeholder.com/150"
                      }
                      alt="Employee Profile"
                      className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover"
                    />
                  </td>
                  <td>{`${employee.firstName} ${employee.lastName}`}</td>
                  <td>{employee.gender || "N/A"}</td>
                  <td>{employee.email}</td>
                  <td>{employee.userType || "N/A"}</td>
                  <td className="text-blue-500">{employee.phone || "N/A"}</td>
                  <td>{employee.dob ? new Date(employee.dob).toLocaleDateString() : "N/A"}</td>
                  <td>{employee.location || "N/A"}</td>
                  <td>{employee.isActive ? "Yes" : "No"}</td>
                  <td className="space-x-2 flex items-center py-2">
                    {/* <button
                      className="bg-[#B6A5C9]/50 text-white p-2 rounded mr-2 hover:bg-[#241763]/60 group"
                      onClick={() => handleEdit(employee.email)}
                    >
                      <Pencil className="text-[#241763] w-4 h-4 group-hover:text-white" />
                    </button>
                    <button
                      className="bg-[#B6A5C9]/50 text-white p-2 rounded hover:bg-[#241763]/60 group"
                      onClick={() => handleDelete(employee.email)}
                    >
                      <Trash2 className="text-[#241763] w-4 h-4 group-hover:text-white" />
                    </button> */}
                    <div
                      onClick={() => toggleView(index)}
                      className="cursor-pointer hover:bg-[#241763]/60 group bg-[#B6A5C9]/50 p-2 rounded"
                    >
                      {expandedRowIndex === index ? (
                        <ChevronUp
                          size={16}
                          className="group-hover:text-white"
                        />
                      ) : (
                        <ChevronDown
                          size={16}
                          className="group-hover:text-white"
                        />
                      )}
                    </div>
                  </td>
                </tr>
                {expandedRowIndex === index && (
                  <tr>
                    <td colSpan="9">
                      <div className="p-4 border rounded-lg bg-gray-50 shadow-inner mt-2">
                        <EmployeeDetailsCard employee={employee} />
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination Component */}
      {!loading && totalCount > 0 && (
        <div className="flex flex-col sm:flex-row justify-between items-center mt-6 gap-4">
          {/* Results Info */}
          <div className="text-sm text-gray-600">
            Showing {startItem} to {endItem} of {totalCount} results
          </div>

          {/* Pagination Controls */}
          <div className="flex items-center space-x-2">
            {/* Previous Button */}
            <button
              onClick={handlePreviousPage}
              disabled={pageNumber === 1}
              className={`flex items-center px-3 py-2 rounded-md text-sm font-medium ${
                pageNumber === 1
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Previous
            </button>

            {/* Page Numbers */}
            <div className="flex items-center space-x-1">
              {totalPages > 1 && getPageNumbers().map((page, index) => (
                <React.Fragment key={index}>
                  {page === '...' ? (
                    <span className="px-3 py-2 text-gray-500">...</span>
                  ) : (
                    <button
                      onClick={() => handlePageClick(page)}
                      className={`px-3 py-2 rounded-md text-sm font-medium ${
                        page === pageNumber
                          ? 'bg-[#241763] text-white'
                          : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  )}
                </React.Fragment>
              ))}
            </div>

            {/* Next Button */}
            <button
              onClick={handleNextPage}
              disabled={pageNumber === totalPages}
              className={`flex items-center px-3 py-2 rounded-md text-sm font-medium ${
                pageNumber === totalPages
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </button>
          </div>

          {/* Page Size Selector */}
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600">Show:</span>
            <select
              value={pageSize}
              onChange={(e) => {
                const newPageSize = parseInt(e.target.value);
                dispatch(setPageNumber(1)); // Reset to first page when changing page size
                dispatch(fetchLmsEmployees({ pageNumber: 1, pageSize: newPageSize }));
              }}
              className="border border-gray-300 rounded px-2 py-1 text-sm"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
            <span className="text-sm text-gray-600">per page</span>
          </div>
        </div>
      )}

      <Toaster />
    </div>
  );
};

export default EmployeeDetails;